package macha.sh.kotlin.pages

import macha.sh.kotlin.HeadlineTextStyle
import macha.sh.kotlin.SubheadlineTextStyle
import macha.sh.kotlin.SiteColors
import macha.sh.kotlin.SiteModifiers
import macha.sh.kotlin.components.layouts.PageLayoutData
import macha.sh.kotlin.components.layouts.SiteContentMaxWidth
import macha.sh.kotlin.SiteTypography
import macha.sh.kotlin.applyFont
import macha.sh.kotlin.integrations.powerGlitch
import macha.sh.kotlin.integrations.PlayMode
import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.AlignSelf
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorPalettes
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import macha.sh.kotlin.components.widgets.MarkdownFileBox
import org.w3c.dom.HTMLPreElement
import org.w3c.dom.HTMLSpanElement
import kotlinx.browser.window
import kotlinx.coroutines.async
import kotlinx.coroutines.await
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.Clock
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime

private const val ASCII_DIR = "text-content/ascii-art/"
private const val IMG_DIR = "img-content/"
private const val TEXT_DIR = "text-content/page-content/home/"

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Home",
            description = "Macha is a generalist developer and DevOps engineer working across software, infrastructure, AI, game development, and digital art.",
            path = "/",
            markdownPath = "/text-content/page-content/home/Welcome-Text.md",
        )
    )
}
@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() { HomeStructure.Content() }

object HomeStructure {
    private val Mods = SiteModifiers.Home
    @Composable
    fun Content() {
        H1(attrs = {
            style {
                property("position", "absolute")
                property("width", "1px")
                property("height", "1px")
                property("padding", "0")
                property("margin", "-1px")
                property("overflow", "hidden")
                property("clip", "rect(0, 0, 0, 0)")
                property("white-space", "nowrap")
                property("border", "0")
            }
        }) {
            Text("Macha — Generalist Developer & DevOps Engineer")
        }
        Box(SiteModifiers.PrimaryPanel, contentAlignment = Alignment.Center) {
            Column(Modifier.fillMaxWidth()) {
                GitRow.Content()
                AsciiRow.Content()
                WelcomeMsg.Content()
            }
        }
    }
}

object GitRow {
    private val Mods = SiteModifiers.Home
    private val dataSource = HeatmapObj.ApiDataSource()

    @Composable
    fun Content() {
        var selectedCell by remember { mutableStateOf<HeatmapObj.HeatCell?>(null) }
        var totalEvents by remember { mutableStateOf(0) }

        Row(modifier = Mods.mainRow()) {
            Avatar()
            Box(modifier = Mods.heatmap(), contentAlignment = Alignment.Center) {
                HeatmapObj.Demo(
                    source = dataSource,
                    weeks = 26,
                    onCellClick = { selectedCell = it },
                    onCellHover = { selectedCell = it },
                    onDataLoaded = { map -> totalEvents = map.values.sum() }
                )
            }
            History(modifier = Mods.gitHistory(), selected = selectedCell, total = totalEvents)
        }
        Column(modifier = Mods.mobileColumn()) {
            Row(modifier = Mods.mobileTopRow()) {
                Avatar()
                Box(modifier = Mods.mobileHeatmap(), contentAlignment = Alignment.Center) {
                    HeatmapObj.Demo(
                        source = dataSource,
                        weeks = 12,
                        onCellClick = { selectedCell = it },
                        onCellHover = { selectedCell = it },
                        onDataLoaded = { map -> totalEvents = map.values.sum() }
                    )
                }
            }
            History(modifier = Mods.mobileGitHistory(), selected = selectedCell, total = totalEvents)
        }
    }

    @Composable
    private fun Avatar() {
        Box(modifier = Mods.pfp(), contentAlignment = Alignment.Center) {
            Img(
                src = IMG_DIR + "Avatar.png",
                alt = "Illustrated portrait of Macha",
                attrs = Modifier.width(100.percent).toAttrs()
            )
        }
    }

    @Composable
    private fun History(modifier: Modifier, selected: HeatmapObj.HeatCell?, total: Int) {
        Box(modifier = modifier) {
            Column(
                modifier = Modifier.fillMaxWidth().gap(0.5.cssRem),
                horizontalAlignment = Alignment.Start
            ) {
                SpanText(
                    "GIT ACTIVITY TRACKER",
                    modifier = Modifier.applyFont(SiteTypography.Subheading).color(SiteColors.Hover)
                )
                SpanText(
                    if (total > 0) "$total total contributions in tracking window" else "Fetching latest commit activity...",
                    modifier = Modifier.applyFont(SiteTypography.BodyS).color(SiteColors.SiteText)
                )
                Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(SiteColors.Border))
                if (selected != null) {
                    SpanText(
                        "${selected.date}: ${selected.count} event" + if (selected.count != 1) "s" else "",
                        modifier = Modifier.applyFont(SiteTypography.Body).color(SiteColors.SiteText)
                    )
                } else {
                    SpanText(
                        "Hover or tap any cell in the heatmap to inspect date and event count.",
                        modifier = Modifier.applyFont(SiteTypography.BodyS).color(SiteColors.SiteText)
                    )
                }
                Row(modifier = Modifier.gap(0.5.cssRem)) {
                    SpanText("[ GitHub (Public + Private) ]", modifier = Modifier.applyFont(SiteTypography.BodyS).color(SiteColors.Hover))
                    SpanText("[ Forgejo (Public + Private) ]", modifier = Modifier.applyFont(SiteTypography.BodyS).color(SiteColors.Hover))
                }
            }
        }
    }
}

object AsciiRow {
    private val StaticASCII = ASCII_DIR + "home-ascii.txt"
    private val Mods = SiteModifiers.Home

    @Composable
    fun Content() {
        val asciiData = LoadAscii.remember()
        val MachaGif = asciiData?.get("macha")
        val IntroGif = asciiData?.get("dothack-opening1")
        //AsciiDisplay.AsciiArtBox(StaticASCII)
        AsciiDisplay.LiveAsciiBox(MachaGif ?: listOf())
    }
}

object AsciiDisplay {
    private val Mods = SiteModifiers.Home

    @Composable
    private fun GlitchStyles() {
        Style {
            ".ascii-glitch" {
                property("position", "relative")
                property("display", "inline-block")
                property("color", "var(--glitch-base, currentColor)")
            }
            // PowerGlitch creates internal layers marked with data-islayer="true".
            // Target them as descendants, not by class on the same element.
            ".ascii-glitch [data-islayer=\"true\"]" {
                property("mix-blend-mode", "screen")
            }
            ".ascii-glitch [data-islayer=\"true\"]:nth-child(3n + 2)" {
                property("color", "var(--glitch-color-1, currentColor)")
            }
            ".ascii-glitch [data-islayer=\"true\"]:nth-child(3n + 3)" {
                property("color", "var(--glitch-color-2, currentColor)")
            }
            ".ascii-glitch [data-islayer=\"true\"]:nth-child(3n + 4)" {
                property("color", "var(--glitch-color-3, currentColor)")
            }
        }
    }
    // Helpers to keep LaunchedEffect blocks tidy
    private suspend fun fetchText(resourcePath: String): String {
        val response = window.fetch(resourcePath).await()
        if (!response.ok) throw IllegalStateException("Could not load ASCII art from ${'$'}resourcePath (HTTP ${'$'}{response.status}).")
        return response.text().await()
    }
    private suspend fun fetchFrames(resourcePaths: List<String>): List<String> = coroutineScope {
        resourcePaths.map { path ->
            async { fetchText(path) }
        }.awaitAll()
    }

    @Composable
    fun AsciiArtBox(resourcePath: String) {
        var art by remember(resourcePath) { mutableStateOf<String?>(null) }
        var error by remember(resourcePath) { mutableStateOf<String?>(null) }
        LaunchedEffect(resourcePath) {
            runCatching { fetchText(resourcePath) }
                .onSuccess { art = it; error = null }
                .onFailure { art = null; error = "Error loading ASCII art: ${'$'}{it.message}" }
        }
        val displayText = art ?: error ?: "Loading ASCII art…"
        Box(modifier = Mods.ascii(), contentAlignment = Alignment.Center) {
            Column() {
                GlitchStyles()
                Div(attrs = Modifier.toAttrs {
                    classes("ascii-glitch")
                    style {
                        property("--glitch-base", SiteColors.BannerGlitch.Base)
                        property("--glitch-color-1", SiteColors.BannerGlitch.ColorA)
                        property("--glitch-color-2", SiteColors.BannerGlitch.ColorB)
                        property("--glitch-color-3", SiteColors.BannerGlitch.ColorC)
                    }
                    powerGlitch {
                        playMode(PlayMode.ALWAYS)
                        timing(durationMs = 5000)
                        glitchTimeSpan(start = 0.05, end = 0.10)
                        shake(velocity = 30, amplitudeX = 0.275, amplitudeY = 0.1)
                        slice(hueRotate = false)
                        hideOverflow(true)
                        createContainers(true)
                    }
                }) {
                    Box(modifier = Mods.asciiBorder()) { 
                        Pre(attrs = Modifier
                        .fontFamily("JetBrains Mono", "Fira Code", "monospace")
                        .fontSize(0.9.cssRem)
                        .lineHeight(1.2)
                        .toAttrs { style {
                            property("white-space", "pre")
                            property("max-width", "100ch")
                            property("overflow-x", "auto")
                        } }) 
                        {
                            Span(attrs = Modifier.toAttrs {
                                classes("ascii-glitch")
                                style {
                                    property("--glitch-base", SiteColors.BannerGlitch.Base)
                                    property("--glitch-color-1", SiteColors.BannerGlitch.ColorA)
                                    property("--glitch-color-2", SiteColors.BannerGlitch.ColorB)
                                    property("--glitch-color-3", SiteColors.BannerGlitch.ColorC)
                                }
                                powerGlitch {
                                    playMode(PlayMode.ALWAYS)
                                    timing(durationMs = 5000)
                                    glitchTimeSpan(start = 0.05, end = 0.10)
                                    shake(velocity = 30, amplitudeX = 0.275, amplitudeY = 0.1)
                                    slice(hueRotate = false)
                                    hideOverflow(true)
                                    createContainers(true)
                                }
                            }) { Text(displayText) }
                        }
                    }
                }
                Box(modifier = Modifier.fillMaxWidth().padding(0.5.cssRem), contentAlignment = Alignment.Center) {
                    SpanText("Welcome to Paradise", modifier = Modifier.applyFont (
                        SiteTypography.Subheading, 
                        fontSize = 2.cssRem,).color(SiteColors.Hover))
                }
            }
        }
    }

    @Composable
    fun LiveAsciiBox(resourcePaths: List<String>, fps: Int = 30) {
        var frames by remember(resourcePaths) { mutableStateOf<List<String>?>(null) }
        var error by remember(resourcePaths) { mutableStateOf<String?>(null) }
        LaunchedEffect(resourcePaths) {
            runCatching { fetchFrames(resourcePaths) }
                .onSuccess { frames = it; error = null }
                .onFailure { error = "Error loading frames: ${'$'}{it.message}"; frames = null }
        }
        Box(modifier = Mods.ascii(), contentAlignment = Alignment.Center) {
            Column() { 
                GlitchStyles()
                Div(attrs = Modifier.toAttrs {
                    classes("ascii-glitch")
                    style {
                        property("--glitch-base", SiteColors.BannerGlitch.Base)
                        property("--glitch-color-1", SiteColors.BannerGlitch.ColorA)
                        property("--glitch-color-2", SiteColors.BannerGlitch.ColorB)
                        property("--glitch-color-3", SiteColors.BannerGlitch.ColorC)
                    }
                    powerGlitch {
                        playMode(PlayMode.ALWAYS)
                        timing(durationMs = 5000)
                        glitchTimeSpan(start = 0.05, end = 0.10)
                        shake(velocity = 30, amplitudeX = 0.275, amplitudeY = 0.1)
                        slice(hueRotate = false)
                        hideOverflow(true)
                        createContainers(true)
                    }
                }) {
                Box(modifier = Mods.asciiBorder()) {
                var spanEl by remember { mutableStateOf<HTMLSpanElement?>(null) }
                DisposableEffect(frames, fps) {
                    var rafId: Int? = null
                    val f = frames
                    if (
                        !window.matchMedia("(prefers-reduced-motion: reduce)").matches &&
                        !f.isNullOrEmpty()
                    ) {
                        var idx = 0
                        var last = 0.0
                        val interval = 1000.0 / fps
                        fun loop(ts: Double) {
                            if (ts - last >= interval) {
                                spanEl?.textContent = f[idx]
                                idx = (idx + 1) % f.size
                                last = ts
                            }
                            rafId = window.requestAnimationFrame(::loop)
                        }
                        rafId = window.requestAnimationFrame(::loop)
                    }
                    onDispose { rafId?.let { window.cancelAnimationFrame(it) } }
                }
                val initialText = when {
                    error != null -> error!!
                    frames == null -> "Loading ASCII frames…"
                    frames!!.isEmpty() -> "No frames to display."
                    else -> frames!!.first()
                }
                Pre(attrs = Modifier
                    .fontFamily("JetBrains Mono", "Fira Code", "monospace")
                    .fontSize(0.9.cssRem)
                    .lineHeight(1.2)
                    .width(100.percent)
                    .maxWidth(100.percent)
                    .toAttrs { style {
                        property("white-space", "pre")
                        property("max-width", "100ch")
                        property("overflow-x", "auto")
                    } }
                ) {
                    Span(attrs = Modifier.toAttrs {
                        classes("ascii-glitch")
                        style {
                            property("--glitch-base", SiteColors.BannerGlitch.Alt)
                            property("--glitch-color-1", SiteColors.BannerGlitch.ColorA)
                            property("--glitch-color-2", SiteColors.BannerGlitch.ColorB)
                            property("--glitch-color-3", SiteColors.BannerGlitch.ColorC)
                        }
                        powerGlitch {
                            playMode(PlayMode.ALWAYS)
                            timing(durationMs = 5000)
                            glitchTimeSpan(start = 0.05, end = 0.10)
                            shake(velocity = 30, amplitudeX = 0.275, amplitudeY = 0.1)
                            slice(hueRotate = false)
                            hideOverflow(true)
                            createContainers(true)
                        }
                        ref { el -> spanEl = el as HTMLSpanElement; onDispose { spanEl = null } }
                    }) { Text(initialText) }
                }
                }
            }
            Box(modifier = Modifier.fillMaxWidth().padding(0.5.cssRem), contentAlignment = Alignment.Center) {
                SpanText("Welcome to Paradise", modifier = Modifier.applyFont (
                    SiteTypography.Subheading,
                    fontSize = 2.cssRem,).color(SiteColors.Hover))
            }}
        }
    }
}

// Load and expose the ASCII manifest (built at compile time under
// text-content/ascii-art/manifest.json). Provides both a suspending loader
// and a small composable helper to fetch + remember the map.
object LoadAscii {
    suspend fun load(url: String = "text-content/ascii-art/manifest.json"): Map<String, List<String>> {
        val resp = window.fetch(url).await()
        if (!resp.ok) throw IllegalStateException("Failed to load manifest (HTTP ${'$'}{resp.status})")
        val txt = resp.text().await()
        val parsed = js("JSON.parse")(txt)
        val result = linkedMapOf<String, List<String>>()
        val keys = js("Object.keys")(parsed) as Array<String>
        for (k in keys) { val arr = parsed[k] as Array<String>; result[k] = arr.toList() }
        return result
    }
    @Composable
    fun remember(url: String = "text-content/ascii-art/manifest.json"): Map<String, List<String>>? {
        var manifest by remember(url) { mutableStateOf<Map<String, List<String>>?>(null) }
        LaunchedEffect(url) {
            runCatching { load(url) }
                .onSuccess { manifest = it }
                .onFailure { console.error("Failed to load ascii manifest: ", it) }
        }
        return manifest
    }
}

object WelcomeMsg {
    private val Mods = SiteModifiers.Home
    @Composable
    fun Content() { Box(modifier = Mods.welcomeBox(), contentAlignment = Alignment.Center) {
        // Render welcome text from a markdown file under resources/public/content
        MarkdownFileBox(resourcePath = TEXT_DIR + "Welcome-Text.md")
    }}
}

object HeatmapObj {
    data class HeatCell(val date: LocalDate, val count: Int)
    private enum class Intensity { None, Low, Mid, High, Max }
    interface DataSource { suspend fun range(from: LocalDate, to: LocalDate): Map<LocalDate, Int> }

    class EmptyDataSource : DataSource {
        override suspend fun range(from: LocalDate, to: LocalDate): Map<LocalDate, Int> = generateSequence(from) { 
            d -> val next = d.plus(1, DateTimeUnit.DAY) ; if (next <= to) next else null }.associateWith { 0 }
    }

    class ApiDataSource(private val url: String = "/api/heatmap") : DataSource {
        override suspend fun range(from: LocalDate, to: LocalDate): Map<LocalDate, Int> {
            val baseMap = generateSequence(from) { d ->
                val next = d.plus(1, DateTimeUnit.DAY)
                if (next <= to) next else null
            }.associateWith { 0 }.toMutableMap()

            return try {
                val resp = window.fetch(url).await()
                val targetResp = if (resp.ok) resp else window.fetch("/text-content/heatmap.json").await()
                if (!targetResp.ok) return baseMap
                val txt = targetResp.text().await()
                val parsed = js("JSON.parse")(txt)
                val days = if (parsed.days != undefined) parsed.days else parsed
                val keys = js("Object.keys")(days) as Array<String>
                for (k in keys) {
                    try {
                        val date = LocalDate.parse(k)
                        if (date in from..to) {
                            val cnt = (days[k] as Number).toInt()
                            baseMap[date] = cnt
                        }
                    } catch (_: Throwable) {}
                }
                baseMap
            } catch (e: Throwable) {
                console.error("Failed to load heatmap data: ", e)
                baseMap
            }
        }
    }

    @Composable
    fun Heatmap(
        cells: Map<LocalDate, Int>,
        onCellClick: (HeatCell) -> Unit = {},
        onCellHover: (HeatCell?) -> Unit = {},
        tooltip: @Composable (HeatCell) -> Unit = {}
    ) {
        Div({
            style {
                property("display", "grid")
                property("grid-auto-flow", "column")
                property("grid-template-rows", "repeat(7, 1fr)")
                property("gap", "4px")
                property("--hm-0", SiteColors.Heatmap.None)
                property("--hm-1", SiteColors.Heatmap.Low)
                property("--hm-2", SiteColors.Heatmap.Mid)
                property("--hm-3", SiteColors.Heatmap.High)
                property("--hm-4", SiteColors.Heatmap.Max)
                property("--hm-border", SiteColors.Heatmap.Border)
            }
            onMouseLeave { onCellHover(null) }
        }) {
            cells.toList().sortedBy { it.first }.forEach { (date, count) ->
                val cell = HeatCell(date, count)
                val bg = colorFor(intensityFor(count))
                Div({
                    style {
                        property("width", "12px")
                        property("height", "12px")
                        property("border-radius", "2px")
                        property("background-color", bg)
                        property("outline", "1px solid var(--hm-border, transparent)")
                        property("cursor", "pointer")
                    }
                    attr("title", "$date: $count event" + if (count != 1) "s" else "")
                    onMouseEnter { onCellHover(cell) }
                    onClick { onCellClick(cell) }
                }) {
                    tooltip(cell)
                }
            }
        }
    }

    @Composable
    fun Demo(
        source: DataSource = ApiDataSource(),
        weeks: Int = 26,
        onCellClick: (HeatCell) -> Unit = {},
        onCellHover: (HeatCell?) -> Unit = {},
        onDataLoaded: (Map<LocalDate, Int>) -> Unit = {},
        tooltip: @Composable (HeatCell) -> Unit = { cell ->
            Span({
                style {
                    property("position", "absolute")
                    property("clip", "rect(0 0 0 0)")
                }
                attr("aria-label", "On ${cell.date} you had ${cell.count} events")
            })
        }
    ) {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val start = today.minus((weeks * 7) - 1, DateTimeUnit.DAY)
        var data by remember { mutableStateOf<Map<LocalDate, Int>>(emptyMap()) }
        LaunchedEffect(start, today) {
            val loaded = source.range(start, today)
            data = loaded
            onDataLoaded(loaded)
        }
        if (data.isEmpty()) {
            Skeleton(rows = 7, cols = weeks)
        } else {
            Heatmap(
                cells = data,
                onCellClick = onCellClick,
                onCellHover = onCellHover,
                tooltip = tooltip
            )
        }
    }

    @Composable
    fun Skeleton(rows: Int, cols: Int) {
        Div({
            style {
                property("display", "grid")
                property("grid-auto-flow", "column")
                property("grid-template-rows", "repeat($rows, 1fr)")
                property("gap", "4px")
                property("--hm-border", SiteColors.Heatmap.Border)
            }
        }) {
            repeat(rows * cols) {
                Div({
                    style {
                        property("width", "12px")
                        property("height", "12px")
                        property("border-radius", "2px")
                        property("outline", "1px solid var(--hm-border, transparent)")
                        property("background", "linear-gradient(90deg,#222 25%,#2a2a2a 37%,#222 63%)")
                        property("background-size", "400% 100%")
                        property("animation", "hm-shimmer 1.2s ease-in-out infinite")
                    }
                })
            }
        }
    }

    private fun intensityFor(count: Int): Intensity = when {
        count <= 0 -> Intensity.None
        count < 3  -> Intensity.Low
        count < 7  -> Intensity.Mid
        count < 12 -> Intensity.High
        else       -> Intensity.Max
    }

    private fun colorFor(i: Intensity): String = when (i) {
        Intensity.None -> "var(--hm-0)"
        Intensity.Low  -> "var(--hm-1)"
        Intensity.Mid  -> "var(--hm-2)"
        Intensity.High -> "var(--hm-3)"
        Intensity.Max  -> "var(--hm-4)"
    }
}
